import { useAuth0 } from "@auth0/auth0-react";
import { datadogRum } from "@datadog/browser-rum";
import { ApiStatusToFileStatus } from "@utils/constants";
import { convertHeadersToObject, graphqlErrorHandler } from "@utils/utils";
import { GraphQLError } from "graphql";
import { useCallback } from "react";

import { FileUploadAndParseProps, ParsingInformation } from "@fronterahealth/frontera-ui-components";

import { FronteraGraphQLClient } from "@api/graphql/graphql-service";
import {
  ApiLearnerFileProcessingFailureCodeChoices,
  ApiLearnerFileStatusChoices,
  CreateIndependentLearnerFileMutation,
  FileTypeEnum,
  GetReportLearnerFilesDocument,
  MarkLearnerFileAsUploadedMutation,
  StartLearnerFileValidationMutation,
  useCreateIndependentLearnerFileMutation,
  useDeleteLearnerFileMutation,
  useMarkLearnerFileAsUploadedMutation,
  useStartLearnerFileValidationMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useEHRClientData } from "@providers/EHRClientProvider";

type ExtendedFileUploadAndParseProps = {
  S3BucketUrlRetriever: (params: {
    fileKind: string;
    fileName: string;
    mimeType: string;
    fileSize: number;
  }) => Promise<{ bucket: string; fileId: string }>;
};

export const useGetS3BucketUrlRetriever: ({
  metadata,
  learnerId,
}: {
  metadata?: object;
  learnerId: string;
}) => ExtendedFileUploadAndParseProps["S3BucketUrlRetriever"] = ({ metadata: _metadata, learnerId }) => {
  const createReportLearnerFileInputMutation = useCreateIndependentLearnerFileMutation({
    throwOnError: false,
  });
  const metadata = _metadata ? JSON.stringify(_metadata) : undefined;
  return async ({ fileKind, fileName, mimeType, fileSize }) => {
    console.debug(
      `%c<FILE-API> | S3BucketURLRetriever | Requesting S3 Bucket URL and creating file record |` +
        `%c fileKind: ${fileKind} fileName: ${fileName} mimeType: ${mimeType} learnerId: ${learnerId} metadata: ${JSON.stringify(metadata)}" `,
      `color:black`,
      `color:red`,
    );

    let res: CreateIndependentLearnerFileMutation | null;
    try {
      res = await createReportLearnerFileInputMutation.mutateAsync({
        input: {
          learnerId: learnerId,
          fileKind: fileKind as FileTypeEnum,
          fileSize,
          mimeType,
          originalFileName: fileName,
          metadata,
        },
      });

      datadogRum.addAction("create-learner-file", {
        fileMetadata: {
          fileKind,
          mimeType,
          fileName,
          metadata,
          fileId: res?.createIndependentLearnerFile?.learnerFile?.id,
        },
      });

      console.debug(
        `<FILE-API> | S3BucketURLRetriever | Received following bucket information | ` + `%c${JSON.stringify(res)}`,
        "color:red;",
      );
      const learnerFile = res?.createIndependentLearnerFile?.learnerFile;
      const bucket = learnerFile?.signedS3Url || "<missing-bucket-id>";
      const fileId = learnerFile?.id || "<missing-file-id>";
      //
      return {
        bucket,
        fileId,
      };
    } catch (err) {
      console.error(
        `<FILE-API> | S3BucketURLRetriever | ERROR Received following bucket information | ` +
          `%c${JSON.stringify(err)}`,
        "color:red;",
      );

      try {
        const errorMessages = graphqlErrorHandler(err as GraphQLError);
        for (const errorMessage of errorMessages) {
          notifyError(errorMessage);
        }
      } catch (parseError) {
        console.error("Failed to parse JSON from error message:", parseError);
      }

      throw new Error(String(err));
    }
  };
};

export const useGetS3BucketUploadCallback: () => FileUploadAndParseProps["S3BucketUploadCallback"] = () => {
  return async (params) => {
    const { file, mimeType, url } = params;
    console.debug(
      `<FILE-API> | S3BucketUploadCallback | Sending file contents directly to S3 through S3 SDK | ` +
        `%c file: ${file} url: ${url} mimeType: ${mimeType}`,
      "color:red;",
    );
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: new Headers({ "Content-Type": mimeType }),
        body: file,
      });

      console.debug(`<FILE-API> | S3BucketUploadCallback | Done sending data to S3`);
      console.debug(``);
      if (response.status === 200) {
        console.debug({ response });
        datadogRum.addAction("learner-file-uploaded-successfully", {
          fileMetadata: {
            fileName: file.name,
            mimeType,
          },
        });
        return;
      } else {
        console.error(`<FILE-API> | S3BucketUploadCallback | Error in sending data`);
        throw new Error(`Non 200 error message: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error("Error calling S3BucketUploadCallback", err);
      throw new Error(String(err));
    }
  };
};

export const useGetReportFileUploadCompleteCallback: () => FileUploadAndParseProps["reportFileUploadCompleteCallback"] =
  () => {
    const { setUploadedFileMeta } = useEHRClientData();
    const markLearnerFileAsUploadedMutation = useMarkLearnerFileAsUploadedMutation({
      throwOnError: false,
    });

    const startLearnerFileValidationMutation = useStartLearnerFileValidationMutation({
      throwOnError: false,
    });

    return async (fileId) => {
      console.debug(
        `<FILE-API> | reportFileUploadCompleteCallback | Reporting file upload complete to FDM | ` +
          `%cfileId: ${fileId}`,
        "color:red;",
      );
      let res: MarkLearnerFileAsUploadedMutation | null;
      let fileValidationRes: StartLearnerFileValidationMutation | null;
      try {
        res = await markLearnerFileAsUploadedMutation.mutateAsync({
          fileId,
        });

        datadogRum.addAction("mark-learner-file-as-uploaded", {
          fileMetadata: {
            fileId,
          },
        });

        console.debug(`<FILE-API> | reportFileUploadCompleteCallback | Done reporting file upload complete to FDM`);
        console.debug(``);
        const learnerFile = res?.markLearnerFileAsUploaded?.learnerFile;
        const status = learnerFile?.status;
        console.debug({ status });
        setUploadedFileMeta({
          id: fileId,
          status: status,
        });

        console.debug(
          `<FILE-API> | startLearnerFileValidation | Reporting to start file parsing to FDM | ` + `%cfileId: ${fileId}`,
          "color:red;",
        );

        fileValidationRes = await startLearnerFileValidationMutation.mutateAsync({
          learnerFileId: fileId,
        });

        console.debug(`<FILE-API> | startLearnerFileValidation | Done reporting file parsing to FDM`);
        console.debug(
          fileValidationRes.startLearnerFileValidation?.status,
          fileValidationRes.startLearnerFileValidation?.learnerFile?.status,
        );

        return;
      } catch (err) {
        console.error(
          `<FILE-API> | reportFileUploadCompleteCallback | ERROR Received following bucket information | ` +
            `%c${JSON.stringify(err)}`,
          "color:red;",
        );
        throw new Error(String(err));
      }
    };
  };

export const useGetFileParseStatusRetriever: () => (
  fileId: string,
) => Promise<ParsingInformation & { failureCode?: ApiLearnerFileProcessingFailureCodeChoices | null }> = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { setUploadedFileMeta } = useEHRClientData();

  return useCallback(
    async (fileId: string) => {
      try {
        const token = await getAccessTokenSilently();
        const authHeaders: HeadersInit = new Headers();
        authHeaders.append("Authorization", `Bearer ${token}`);
        const headers = convertHeadersToObject(authHeaders);
        const response = await FronteraGraphQLClient.request(GetReportLearnerFilesDocument, { fileId }, headers);

        const learnerFileStatusContext = response?.getReportLearnerFiles?.edges?.[0]?.node;

        const apiStatus: ApiLearnerFileStatusChoices =
          learnerFileStatusContext?.status || ("<missing-status" as ApiLearnerFileStatusChoices);
        const status = ApiStatusToFileStatus[apiStatus];

        setUploadedFileMeta({
          id: fileId,
          status: apiStatus,
        });

        if (status === "parsing-failed") {
          datadogRum.addError("file-parsing-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          datadogRum.addAction("file-parsing-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
        }
        if (
          status === "uploading-failed-bucket-upload" ||
          status === "uploading-failed-upload-notification" ||
          status === "uploading-failed-url-retrieval"
        ) {
          datadogRum.addError("file-upload-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
          datadogRum.addAction("file-upload-failed", {
            fileMetadata: {
              fileId,
              status,
            },
          });
        }
        if (status === "done") {
          datadogRum.addAction("file-parsing-succeeded", {
            fileMetadata: {
              fileId,
              status,
            },
          });
        }
        console.debug(`<FILE-API> | fileParseStatusRetriever | Got the following parse status from FDM | ${status}`);
        console.debug(``);
        return {
          fileId,
          status,
          failureReason: learnerFileStatusContext?.processingFailureReason,
          failureCode: learnerFileStatusContext?.processingFailureCode,
        };
      } catch (err) {
        console.error(
          `<FILE-API> | fileParseStatusRetriever | ERROR Received error trying to get parse status | ` +
            `%c${JSON.stringify(err)}`,
          "color:red;",
        );
        throw new Error(String(err));
      }
    },
    [isAuthenticated],
  );
};

export const useGetRemoveFileCallback: () => FileUploadAndParseProps["removeFileCallback"] = () => {
  const deleteLearnerFileMutation = useDeleteLearnerFileMutation({
    throwOnError: false,
  });

  const { setUploadedFileMeta } = useEHRClientData();

  return async (fileId: string) => {
    try {
      await deleteLearnerFileMutation.mutateAsync({ fileId });
      notifySuccess(`Successfully deleted file`);
      setUploadedFileMeta(null);
    } catch (err) {
      console.error("Error with deleting file", err);
      notifyError(`Failed to delete file. Something went wrong on our end`);
    }
  };
};
